import React, { useEffect, useState } from "react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import SEO from "./SEO";

/**
 * Shared layout for legal pages (Privacy, Terms, Compliance, Security).
 * Renders a sticky table of contents on desktop and rich content on the right.
 *
 * Props:
 *   title         — main page heading
 *   eyebrow       — small uppercase label above the heading
 *   lastUpdated   — display string for "Last Updated: ..."
 *   sections      — [{ id, heading, content: ReactNode }]
 *   seoDescription — meta description for search engines
 *   path          — canonical path (e.g. "/privacy")
 */
export default function LegalLayout({ title, eyebrow, lastUpdated, sections, seoDescription, path }) {
  const [activeId, setActiveId] = useState(sections[0]?.id);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 }
    );
    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [sections]);

  return (
    <div className="min-h-screen bg-white" data-testid="legal-page">
      <SEO
        title={`${title} — BuyerBeacon Pro`}
        description={seoDescription || `${title} for BuyerBeacon Pro. Last updated ${lastUpdated}.`}
        path={path}
      />
      <Navbar />
      <main className="pt-28">
        {/* Hero */}
        <section className="relative overflow-hidden border-b border-slate-100 pb-12">
          <div className="pointer-events-none absolute left-1/2 top-[60px] -z-10 h-[280px] w-[600px] -translate-x-1/2 rounded-full bg-[#d4af37]/10 blur-[120px]" />
          <div className="mx-auto max-w-5xl px-5 lg:px-8">
            <p
              className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500"
              data-testid="legal-eyebrow"
            >
              {eyebrow}
            </p>
            <h1
              className="mt-3 text-4xl font-medium tracking-tight text-[#1e1b4b] sm:text-5xl lg:text-6xl"
              data-testid="legal-title"
            >
              {title}
            </h1>
            <p
              className="mt-4 text-sm text-slate-500"
              data-testid="legal-last-updated"
            >
              Last Updated: {lastUpdated}
            </p>
          </div>
        </section>

        {/* Body */}
        <section className="mx-auto max-w-7xl px-5 py-14 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-[260px_1fr]">
            {/* TOC */}
            <aside className="hidden lg:block">
              <div className="sticky top-28">
                <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                  Contents
                </div>
                <ul className="mt-4 flex flex-col gap-2 border-l border-slate-200 pl-4">
                  {sections.map((s) => (
                    <li key={s.id}>
                      <a
                        href={`#${s.id}`}
                        data-testid={`legal-toc-${s.id}`}
                        className={`block text-sm leading-snug transition-colors ${
                          activeId === s.id
                            ? "font-semibold text-[#1e1b4b]"
                            : "text-slate-500 hover:text-[#1e1b4b]"
                        }`}
                      >
                        {s.heading}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>

            {/* Content */}
            <article
              className="max-w-3xl text-[15px] leading-[1.75] text-slate-700"
              data-testid="legal-content"
            >
              {sections.map((s) => (
                <section
                  key={s.id}
                  id={s.id}
                  className="scroll-mt-28 border-b border-slate-100 py-8 first:pt-0 last:border-b-0"
                  data-testid={`legal-section-${s.id}`}
                >
                  <h2 className="mb-4 text-2xl font-semibold tracking-tight text-[#1e1b4b]">
                    {s.heading}
                  </h2>
                  <div className="legal-prose">{s.content}</div>
                </section>
              ))}

              <div className="mt-10 rounded-xl border border-slate-200 bg-slate-50/60 p-6">
                <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                  Contact
                </div>
                <div className="mt-3 text-sm text-slate-700">
                  <div className="font-semibold text-[#1e1b4b]">BuyerBeacon Pro</div>
                  <div>32 N Gould St., Ste R</div>
                  <div>Sheridan, WY 82801</div>
                  <a
                    href="mailto:admin@buyerbeacon.pro"
                    className="mt-2 inline-block text-[#1e1b4b] underline-offset-4 hover:underline"
                    data-testid="legal-contact-email"
                  >
                    admin@buyerbeacon.pro
                  </a>
                </div>
              </div>
            </article>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
